import React from 'react'
import { TaskService } from '@duck/api-definitions'
import { Badge } from '@duck/ui'
import { getDifficultyBadgeColor, type Column } from './taskAdmin.utils'
import { taskDifficultyDisplayMap } from '@/utils/challengeData.utils'
import { TasksTableSkeleton } from './TasksTableSkeleton'
import { useTar } from '@/modules'
import { useLoadMoreOnScroll } from '@/hooks/useLoadMoreOnScroll'

interface TasksTableProps {
  tasks: TaskService.Task[]
  totalTasks: number
  visibleColumns: Column[]
  sortField: keyof TaskService.Task
  sortDirection: 'asc' | 'desc'
  onSort: (field: keyof TaskService.Task) => void
  onTaskClick?: (task: TaskService.Task) => void
  loadingMode?: boolean
  hasMore?: boolean
  onLoadMore?: () => void
  isPageLoading?: boolean
}

export const TasksTable: React.FC<TasksTableProps> = ({
  tasks,
  totalTasks,
  visibleColumns,
  sortField,
  sortDirection,
  onSort,
  onTaskClick,
  loadingMode = false,
  hasMore = false,
  onLoadMore,
  isPageLoading = false,
}) => {
  const { t } = useTar()

  const { sentinelRef: loadMoreSentinelRef, sentinelIndex: loadMoreSentinelIndex } = useLoadMoreOnScroll<HTMLTableRowElement>({
    hasMore,
    onLoadMore,
    isLoading: isPageLoading,
    disabled: loadingMode,
    itemCount: tasks.length,
    offsetFromEnd: 10,
  })

  const cellRenderers: Partial<Record<keyof TaskService.Task, (task: TaskService.Task) => React.ReactNode>> = {
    name: (task) => (
      <p className="text-sm font-medium text-foreground">
        {task.name}
      </p>
    ),
    teaser: (task) => (
      <p className="text-sm text-muted-foreground truncate max-w-md" title={task.teaser}>
        {task.teaser}
      </p>
    ),
    body: (task) => (
      <p className="text-sm text-muted-foreground truncate max-w-md" title={task.body}>
        {task.body}
      </p>
    ),
    difficulty: (task) => (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getDifficultyBadgeColor(task.difficulty)}`}>
        {t(taskDifficultyDisplayMap[task.difficulty])}
      </span>
    ),
    availabilityWeight: (task) => (
      <span className="text-sm text-muted-foreground">
        {task.availabilityWeight ?? '—'}
      </span>
    ),
    description: (task) => (
      <p className="text-sm text-muted-foreground truncate max-w-md" title={task.description}>
        {task.description}
      </p>
    ),
    accessGroups: (task) => (
      <div className="max-w-xs">
        {task.accessGroups && task.accessGroups.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {task.accessGroups.map((group, index) => (
              <Badge key={index} variant="outline" className="bg-accent/10 text-accent border-accent/20">
                {group}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground italic">{t('common.noGroups')}</span>
        )}
      </div>
    ),
  }

  const renderCellContent = (task: TaskService.Task, columnKey: keyof TaskService.Task) => {
    const renderer = cellRenderers[columnKey]
    return renderer ? renderer(task) : null
  }

  return (
    <div className="bg-card shadow overflow-hidden rounded-md sm:rounded-md border border-border">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-foreground">
          {t('tasks.allTasks')}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {loadingMode ? t('common.loading') : t('common.XRecords', { count: tasks.length })}
        </p>
      </div>

      <div className="overflow-x-auto overflow-y-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary/5">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 text-start text-xs font-medium text-muted-foreground tracking-wider"
                >
                  {column.sortable ? (
                    <button
                      onClick={() => onSort(column.key as keyof TaskService.Task)}
                      className="flex items-center gap-1 hover:text-foreground transition-colors"
                    >
                      <span>{t(column.label)}</span>
                      {sortField === column.key && (
                        <span className="text-accent">
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  ) : (
                    t(column.label)
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {loadingMode ? (
              <TasksTableSkeleton visibleColumns={visibleColumns} rowCount={8} />
            ) : (
              tasks.map((task, index) => {
                const isLoadMoreSentinel = hasMore && !loadingMode && index === loadMoreSentinelIndex

                return (
                  <tr
                    key={task.id}
                    data-testid={`task-row-${task.name}`}
                    ref={isLoadMoreSentinel ? loadMoreSentinelRef : undefined}
                    className={`hover:bg-secondary/5 ${onTaskClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onTaskClick?.(task)}
                  >
                    {visibleColumns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {renderCellContent(task, column.key)}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {hasMore && !loadingMode && (
        <div className="flex items-center justify-center py-4">
          {isPageLoading && (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent"></div>
              <span className="ms-2 text-sm text-muted-foreground">{t('common.loading')}</span>
            </div>
          )}
        </div>
      )}

      {!loadingMode && tasks.length === 0 && (
        <div className="text-center py-12">
          <p className="text-sm text-muted-foreground">{t('tasks.noTasksMatchFilters')}</p>
        </div>
      )}
    </div>
  )
}
